// src/pages/MentorSchedulePage.tsx
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  Clock,
  Video,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader,
  Award,
  User,
  Mail,
  ChevronDown,
} from "lucide-react";
import { mentorshipApi } from "../lib";

interface Session {
  _id: string;
  student: {
    _id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    email?: string;
    avatar?: string;
  };
  topic: string;
  notes?: string;
  scheduledAt: string;
  duration: number;
  status: "pending" | "confirmed" | "completed" | "cancelled";
  meetingLink?: string;
}

const MentorSchedulePage = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await mentorshipApi.getMySessions({
        role: "mentor",
        limit: 1000,
      });
      if (response.success) {
        setSessions(response.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days: (number | null)[] = [];

    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  const getSessionsForDay = (day: number) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return sessions.filter((session) => {
      const sessionDate = new Date(session.scheduledAt);
      return (
        sessionDate.getDate() === day &&
        sessionDate.getMonth() === month &&
        sessionDate.getFullYear() === year
      );
    });
  };

  const getSessionsForSelectedDate = () => {
    if (!selectedDate) return [];
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();
    return sessions.filter((session) => {
      const sessionDate = new Date(session.scheduledAt);
      return (
        sessionDate.getDate() === day &&
        sessionDate.getMonth() === month &&
        sessionDate.getFullYear() === year
      );
    });
  };

  const getStudentName = (student: Session["student"]) => {
    if (student?.firstName && student?.lastName) {
      return `${student.firstName} ${student.lastName}`;
    }
    return student?.name || student?.email || "Student";
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const hasSessions = (day: number) => {
    return getSessionsForDay(day).length > 0;
  };

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
    setSelectedDate(null);
  };

  const handleDayClick = (day: number) => {
    setSelectedDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), day),
    );
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const goToUpcomingPage = (page: number) => {
    setUpcomingPage(page);
  };

  const upcomingSessions = sessions.filter(
    (s) => s.status === "confirmed" || s.status === "pending",
  );
  const totalUpcomingPages = Math.max(
    1,
    Math.ceil(upcomingSessions.length / itemsPerPage),
  );
  const safeUpcomingPage = Math.min(upcomingPage, totalUpcomingPages);
  const startIndex = (safeUpcomingPage - 1) * itemsPerPage;
  const paginatedUpcomingSessions = upcomingSessions.slice(
    startIndex,
    startIndex + itemsPerPage,
  );
  const selectedDateSessions = getSessionsForSelectedDate();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-xs text-gray-500">Loading schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-3 mx-auto max-w-7xl sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
              <CalendarIcon className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-gray-900 md:text-xl">
              My Schedule
            </h1>
          </div>
          <p className="text-[10px] text-gray-500 ml-10 md:text-xs">
            View and manage your mentorship sessions
          </p>
        </div>

        {/* Calendar and Sessions */}
        <div className="flex flex-col gap-4">
          {/* Calendar Section */}
          <div className="p-3 overflow-x-auto bg-white border border-gray-100 shadow-sm rounded-xl">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={previousMonth}
                className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors shrink-0"
              >
                <ChevronLeft className="w-4 h-4 text-orange-600" />
              </button>
              <h2 className="px-2 text-sm font-semibold text-orange-600 truncate">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button
                onClick={nextMonth}
                className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors shrink-0"
              >
                <ChevronRight className="w-4 h-4 text-orange-600" />
              </button>
            </div>

            {/* Calendar Grid - Responsive */}
            <div className="min-w-[280px]">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-0.5 mb-1">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center text-[9px] font-medium text-orange-600 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-0.5">
                {getDaysInMonth(currentDate).map((day, index) => (
                  <div key={index}>
                    {day !== null ? (
                      <button
                        onClick={() => handleDayClick(day)}
                        className={`w-full aspect-square p-1 rounded-lg transition-all relative ${
                          isSelected(day)
                            ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                            : isToday(day)
                              ? "bg-orange-100 text-orange-700 font-semibold"
                              : hasSessions(day)
                                ? "bg-orange-50 text-orange-600 hover:bg-orange-100"
                                : "hover:bg-gray-50"
                        }`}
                      >
                        <span
                          className={`text-[11px] sm:text-xs ${isSelected(day) ? "text-white" : isToday(day) ? "text-orange-700" : "text-gray-900"}`}
                        >
                          {day}
                        </span>
                        {hasSessions(day) && (
                          <span
                            className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                              isSelected(day) ? "bg-white" : "bg-orange-500"
                            }`}
                          />
                        )}
                      </button>
                    ) : (
                      <div className="w-full aspect-square" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sessions for Selected Date */}
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <h3 className="text-[11px] font-semibold text-gray-900 mb-3 truncate">
              {selectedDate
                ? selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })
                : "Select a date to view sessions"}
            </h3>

            {selectedDate ? (
              selectedDateSessions.length > 0 ? (
                <div className="space-y-3">
                  {selectedDateSessions.map((session) => (
                    <div
                      key={session._id}
                      className="p-3 border border-orange-100 rounded-lg bg-orange-50"
                    >
                      {/* Student Info */}
                      <div className="flex items-start gap-2 mb-2">
                        <div className="w-8 h-8 overflow-hidden bg-orange-200 rounded-full shrink-0">
                          {session.student?.avatar ? (
                            <img
                              src={session.student.avatar}
                              alt=""
                              className="object-cover w-full h-full"
                            />
                          ) : (
                            <div className="flex items-center justify-center w-full h-full text-xs font-medium text-orange-700">
                              {(
                                session.student?.firstName?.[0] || "S"
                              ).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {getStudentName(session.student)}
                          </p>
                          <p className="text-xs text-gray-600 break-words line-clamp-2">
                            {truncateText(session.topic, 80)}
                          </p>
                        </div>
                      </div>

                      {/* Session Details */}
                      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 shrink-0" />
                          <span className="whitespace-nowrap">
                            {formatTime(session.scheduledAt)} (
                            {session.duration} min)
                          </span>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full ${
                            session.status === "confirmed"
                              ? "bg-green-100 text-green-700"
                              : session.status === "completed"
                                ? "bg-blue-100 text-blue-700"
                                : session.status === "cancelled"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {session.status}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      {session.meetingLink &&
                        session.status === "confirmed" && (
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <button
                              onClick={() =>
                                navigate(`/video-room/${session._id}`)
                              }
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-orange-600 to-orange-500 text-white text-xs font-medium rounded-lg hover:shadow-md transition-all"
                            >
                              <Video className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">Join In-App</span>
                            </button>
                            <a
                              href={session.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-all"
                            >
                              <Video className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">Join Meet</span>
                            </a>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-500">
                    No sessions scheduled for this day
                  </p>
                </div>
              )
            ) : (
              <div className="py-8 text-center">
                <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">
                  Tap on any date to view scheduled sessions
                </p>
              </div>
            )}
          </div>
        </div>

        {/* All Upcoming Sessions Section */}
        <div className="p-3 mt-4 bg-white border border-gray-100 shadow-sm rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[11px] font-semibold text-gray-900">
              All Upcoming Sessions
            </h3>
            <span className="text-[10px] text-gray-500">
              {upcomingSessions.length} total
            </span>
          </div>

          {upcomingSessions.length === 0 ? (
            <p className="py-4 text-sm text-center text-gray-500">
              No upcoming sessions scheduled
            </p>
          ) : (
            <>
              <div className="space-y-3">
                {paginatedUpcomingSessions.map((session) => (
                  <div
                    key={session._id}
                    className="p-3 border border-gray-100 rounded-lg bg-gray-50"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center flex-1 min-w-0 gap-2">
                          <div className="w-8 h-8 overflow-hidden bg-orange-100 rounded-full shrink-0">
                            {session.student?.avatar ? (
                              <img
                                src={session.student.avatar}
                                alt=""
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <div className="flex items-center justify-center w-full h-full text-xs font-medium text-orange-700">
                                {(
                                  session.student?.firstName?.[0] || "S"
                                ).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {getStudentName(session.student)}
                            </p>
                            <p className="text-xs text-gray-600 line-clamp-2">
                              {truncateText(session.topic, 60)}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${
                            session.status === "confirmed"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {session.status}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <CalendarIcon className="w-3 h-3 shrink-0" />
                          <span className="truncate">
                            {new Date(session.scheduledAt).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric" },
                            )}{" "}
                            at {formatTime(session.scheduledAt)}
                          </span>
                        </div>
                        {session.meetingLink &&
                          session.status === "confirmed" && (
                            <a
                              href={session.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs font-medium text-orange-600 hover:underline shrink-0"
                            >
                              Join Meeting →
                            </a>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {totalUpcomingPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => goToUpcomingPage(safeUpcomingPage - 1)}
                    disabled={safeUpcomingPage <= 1}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    Prev
                  </button>
                  <span className="text-[10px] text-gray-500">
                    Page {safeUpcomingPage} of {totalUpcomingPages}
                  </span>
                  <button
                    onClick={() => goToUpcomingPage(safeUpcomingPage + 1)}
                    disabled={safeUpcomingPage >= totalUpcomingPages}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium rounded-lg border border-gray-200 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentorSchedulePage;
